import { useCallback } from 'react';
import cn from 'classnames';
import { useIntl } from 'react-intl';
import { useAppContext } from 'contexts/AppContext';
import requireValue from 'utils/requireValue';
import s from './Nav.module.scss';

export interface INavTab {
  label: string;
}

/** `currentStep` and `goToStep` are injected by StepWizard (the `nav` element is cloned with them). */
export interface INavProps {
  tabs: ReadonlyArray<INavTab>;
  currentStep?: number;
  goToStep?: (step: number) => void;
  clickableTabStep?: number;
  step?: number | null;
  isEditMode?: boolean;
}

// The comparisons coerce like the former JS relational operators (undefined -> NaN, null -> 0).
const Nav = ({ currentStep, tabs, goToStep, clickableTabStep, step, isEditMode }: INavProps) => {
  const { formatMessage: t } = useIntl();
  const { theme } = useAppContext();

  const getClassNamesForTab = useCallback(
    (tabIndex: number) => {
      let classnames = 'tab';
      if (currentStep === tabIndex + 1) classnames += ' activeTab';
      if (Number(currentStep) > tabIndex + 1 || Number(step) > tabIndex + 1 || isEditMode)
        classnames += ' completedTab';
      if (isEditMode || (Number(step) >= tabIndex + 1 && Number(step) >= Number(clickableTabStep)))
        classnames += ' clickableTab';
      return classnames;
    },
    [currentStep, isEditMode, step, clickableTabStep],
  );

  const onTabClick = useCallback(
    (tabIndex: number) => {
      if (isEditMode || (Number(step) >= Number(clickableTabStep) && tabIndex <= Number(step))) {
        requireValue(goToStep, 'goToStep')(tabIndex);
      }
    },
    [goToStep, isEditMode, clickableTabStep, step],
  );

  return (
    <div className={cn('tabs', s.tabs, theme === 'green' ? 'green-tabs' : '')}>
      {tabs.map(({ label }, i) => {
        const classnames = getClassNamesForTab(i);
        return (
          <button
            key={label}
            onClick={() => onTabClick(i + 1)}
            type="button"
            className={classnames}
            aria-label={t({ id: label })}
          >
            <div className="circle">
              <span className="step">{i + 1}</span>
            </div>
            <div className="title">{t({ id: label })}</div>
          </button>
        );
      })}
    </div>
  );
};

export default Nav;
